import { useState } from "react";
import { toast } from "sonner";
import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle, Color } from "@tiptap/extension-text-style";
import { Bold, Italic, Underline, Strikethrough, Heading2, Heading3, List, ListOrdered, Quote, Minus, Link2, Unlink, Undo2, Redo2, RemoveFormatting } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";

const COLOURS = ["#111111", "#E60000", "#B45309", "#047857", "#1D4ED8", "#6B7280"];

const Btn = ({ on, active, label, testId, children }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={on} title={label} aria-label={label} data-testid={testId}
    className={`h-8 w-8 grid place-items-center rounded-md transition-colors ${active ? "bg-[#111] text-white" : "hover:bg-gray-200 text-gray-700"}`}>{children}</button>
);

const LinkTool = ({ editor }) => {
  const [url, setUrl] = useState("");
  const apply = () => {
    const v = url.trim();
    if (v) {
      if (/^[a-z][a-z0-9+.-]*:/i.test(v) && !/^(https:|mailto:|tel:)/i.test(v)) { toast.error("Links must start with https://, mailto: or tel:"); return; }
      editor.chain().focus().extendMarkRange("link").setLink({ href: /^(https:|mailto:|tel:)/i.test(v) ? v : `https://${v}`, target: "_blank" }).run();
    }
    setUrl("");
  };
  return (
    <Popover>
      <PopoverTrigger asChild><button type="button" title="Add link" aria-label="Add link" data-testid="rte-link" className="h-8 w-8 grid place-items-center rounded-md hover:bg-gray-200 text-gray-700"><Link2 className="w-4 h-4" /></button></PopoverTrigger>
      <PopoverContent className="w-72 p-3 space-y-2">
        <div className="text-xs font-semibold text-gray-600">Link address for the selected text</div>
        <Input value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); apply(); } }} placeholder="https://gamls.org" className="h-9" data-testid="rte-link-input" />
        <button type="button" onClick={apply} className="w-full h-8 rounded-md bg-[#E60000] text-white text-xs font-semibold" data-testid="rte-link-apply">Apply link</button>
      </PopoverContent>
    </Popover>
  );
};

const ColourTool = ({ editor, current }) => (
  <div className="flex items-center gap-1 px-1" data-testid="rte-colours">
    {COLOURS.map((c) => (
      <button key={c} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().setColor(c).run()} title={`Text colour ${c}`} data-testid={`rte-colour-${c.slice(1)}`}
        className={`w-5 h-5 rounded-full border-2 ${current === c ? "border-[#111]" : "border-white shadow"}`} style={{ background: c }} />
    ))}
  </div>
);

const Toolbar = ({ editor }) => {
  const s = useEditorState({ editor, selector: ({ editor: e }) => ({
    bold: e.isActive("bold"), italic: e.isActive("italic"), underline: e.isActive("underline"), strike: e.isActive("strike"),
    h2: e.isActive("heading", { level: 2 }), h3: e.isActive("heading", { level: 3 }), ul: e.isActive("bulletList"), ol: e.isActive("orderedList"),
    quote: e.isActive("blockquote"), link: e.isActive("link"), colour: e.getAttributes("textStyle").color }) });
  const c = () => editor.chain().focus();
  return (
    <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b bg-[#F8F9FA] sticky top-0 z-10" data-testid="rte-toolbar">
      <Btn on={() => c().toggleBold().run()} active={s.bold} label="Bold" testId="rte-bold"><Bold className="w-4 h-4" /></Btn>
      <Btn on={() => c().toggleItalic().run()} active={s.italic} label="Italic" testId="rte-italic"><Italic className="w-4 h-4" /></Btn>
      <Btn on={() => c().toggleUnderline().run()} active={s.underline} label="Underline" testId="rte-underline"><Underline className="w-4 h-4" /></Btn>
      <Btn on={() => c().toggleStrike().run()} active={s.strike} label="Strikethrough" testId="rte-strike"><Strikethrough className="w-4 h-4" /></Btn>
      <span className="w-px h-5 bg-gray-300 mx-1" />
      <Btn on={() => c().toggleHeading({ level: 2 }).run()} active={s.h2} label="Large heading" testId="rte-h2"><Heading2 className="w-4 h-4" /></Btn>
      <Btn on={() => c().toggleHeading({ level: 3 }).run()} active={s.h3} label="Small heading" testId="rte-h3"><Heading3 className="w-4 h-4" /></Btn>
      <Btn on={() => c().toggleBulletList().run()} active={s.ul} label="Bulleted list" testId="rte-ul"><List className="w-4 h-4" /></Btn>
      <Btn on={() => c().toggleOrderedList().run()} active={s.ol} label="Numbered list" testId="rte-ol"><ListOrdered className="w-4 h-4" /></Btn>
      <Btn on={() => c().toggleBlockquote().run()} active={s.quote} label="Highlighted box" testId="rte-quote"><Quote className="w-4 h-4" /></Btn>
      <Btn on={() => c().setHorizontalRule().run()} label="Divider line" testId="rte-hr"><Minus className="w-4 h-4" /></Btn>
      <span className="w-px h-5 bg-gray-300 mx-1" />
      <LinkTool editor={editor} />
      {s.link && <Btn on={() => c().unsetLink().run()} label="Remove link" testId="rte-unlink"><Unlink className="w-4 h-4" /></Btn>}
      <ColourTool editor={editor} current={s.colour} />
      <span className="w-px h-5 bg-gray-300 mx-1" />
      <Btn on={() => c().unsetAllMarks().clearNodes().run()} label="Clear formatting" testId="rte-clear"><RemoveFormatting className="w-4 h-4" /></Btn>
      <Btn on={() => c().undo().run()} label="Undo" testId="rte-undo"><Undo2 className="w-4 h-4" /></Btn>
      <Btn on={() => c().redo().run()} label="Redo" testId="rte-redo"><Redo2 className="w-4 h-4" /></Btn>
    </div>
  );
};

export const RichEditor = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [StarterKit.configure({ link: { openOnClick: false, protocols: ["https", "mailto", "tel"] } }), TextStyle, Color],
    content: value,
    immediatelyRender: true,
    editorProps: { attributes: { class: "rich-content min-h-[320px] p-5 focus:outline-none", "data-testid": "rte-content" } },
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? "" : e.getHTML()),
  });
  if (!editor) { return null; }
  return (
    <div className="border border-[#E5E7EB] rounded-lg bg-white overflow-hidden focus-within:border-[#E60000] transition-colors" data-testid="rich-editor">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
};
